import { FC, useMemo, useState } from 'react';

import { updateGiftChoiceUnit } from '@shared/api/giftChoiceUnit/giftChoiceUnit.controller';
import {
  SendReselectNoticeResult,
  sendReselectNotice,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import { GiftCouponForRedemptionDashboard } from '@shared/api/giftCoupon/giftCoupon.interface';
import { updateGiftItemInfo } from '@shared/api/giftItemInfo/giftItemInfo.controller';
import { useAuth } from '@shared/state/hooks/useAuth';
import {
  giftCouponReselectPreviewUrl,
  giftCouponReselectRecipientUrl,
  giftRecipientBaseUrl,
} from '@shared/utils/giftRecipientLink.util';

import {
  RESELECT_NOTICE_DEFAULT_MESSAGE,
  RESELECT_NOTICE_PRESETS,
} from '../utils/reselectNoticePresets';

/**
 * 재선택 기한 기본값(일).
 * 전자상거래법상 공급 곤란 통지 후 환급 의무가 있어 기한을 반드시 고지한다.
 * 서버도 미지정 시 같은 값(14일)을 적용하지만, 관리자가 문자 문구를 미리 확인할 수 있도록
 * FE에서 날짜를 계산해 함께 보낸다.
 */
const DEFAULT_DEADLINE_DAYS = 14;

const REASON_LABEL: Record<string, string> = {
  NO_PHONE: '수신번호 없음',
  NOT_FOUND: '쿠폰 없음',
  INVALID_STATUS: '발송대기 상태 아님',
};

const toDateInputValue = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const fmtPhone = (phone: string | undefined) => {
  if (!phone) return '';
  const n = phone.replace(/[^0-9]/g, '');
  if (n.length === 11) return `${n.slice(0, 3)}-${n.slice(3, 7)}-${n.slice(7)}`;
  return phone;
};

const itemNameOf = (c: GiftCouponForRedemptionDashboard): string =>
  typeof c.giftItem === 'object'
    ? c.giftItem?.name || c.giftItem?.giftItemInfo?.name || '-'
    : '-';

const unitNameOf = (c: GiftCouponForRedemptionDashboard): string =>
  c.selectedChoiceUnit && typeof c.selectedChoiceUnit === 'object'
    ? c.selectedChoiceUnit.name
    : '';

/** 선택형 쿠폰이면 선택된 옵션(ChoiceUnit) 문서 — 품절 대상의 1순위 */
const choiceUnitOf = (c: GiftCouponForRedemptionDashboard) =>
  c.selectedChoiceUnit && typeof c.selectedChoiceUnit === 'object'
    ? c.selectedChoiceUnit
    : null;

/** 선택형이 아니면 상품(GiftItemInfo) 자체가 품절 대상 */
const itemInfoOf = (c: GiftCouponForRedemptionDashboard) => {
  const item = typeof c.giftItem === 'object' ? c.giftItem : null;
  const info = item?.giftItemInfo;
  return info && typeof info === 'object' ? info : null;
};

/**
 * 품절 처리 대상 = 선택 옵션(있으면) / 없으면 상품 원본.
 * key 는 판매상태 오버라이드 맵과 행 배지에서 함께 쓴다.
 */
type SoldOutTarget = {
  key: string;
  kind: 'choiceUnit' | 'itemInfo';
  id: string;
  name: string;
  /** 부가 표기 — 옵션이면 소속 상품명, 상품이면 브랜드명 */
  subName: string;
  count: number;
  enabled: boolean;
};

const targetKeyOf = (c: GiftCouponForRedemptionDashboard): string | null => {
  const unit = choiceUnitOf(c);
  if (unit) return `choiceUnit:${unit._id}`;
  const info = itemInfoOf(c);
  return info ? `itemInfo:${info._id}` : null;
};

/** 구매자(상주) 이름 — 환불 안내 문장에 들어간다 */
const buyerNameOf = (c?: GiftCouponForRedemptionDashboard): string => {
  const order = c && typeof c.giftOrder === 'object' ? c.giftOrder : null;
  return order?.bugo?.member?.fullName?.trim() || '';
};

interface ReselectNoticeModalProps {
  /** 품절 안내를 보낼 발송대기 쿠폰들 */
  coupons: GiftCouponForRedemptionDashboard[];
  onClose: () => void;
  /** 발송 후(닫기 시) 호출 — 부모가 목록 갱신·선택 해제 */
  onSent?: () => void;
}

/**
 * 품절 안내 일괄 발송 모달.
 * 선택한 발송대기 쿠폰을 재선택 안내 상태로 전환하고, 수령자에게 재선택 링크를 문자로 보낸다.
 *
 * 문구는 **사실만** 쓴다 — 원인이 판매처 단종인지 공급사 품절인지에 따라 프리셋을 고르고,
 * 필요하면 직접 편집한다. (`../utils/reselectNoticePresets`)
 * 스펙: docs/gift-coupon-reselect-soldout-spec.md
 */
export const ReselectNoticeModal: FC<ReselectNoticeModalProps> = ({
  coupons,
  onClose,
  onSent,
}) => {
  const [presetId, setPresetId] = useState<string>('');
  const [message, setMessage] = useState(RESELECT_NOTICE_DEFAULT_MESSAGE);
  const [reason, setReason] = useState('');
  const [deadline, setDeadline] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + DEFAULT_DEADLINE_DAYS);
    return toDateInputValue(d);
  });
  const [baseUrl, setBaseUrl] = useState(window.location.origin);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SendReselectNoticeResult | null>(null);

  const { sellerRole } = useAuth();
  /** 이 모달에서 바꾼 판매상태 — 목록 데이터는 갱신되지 않으므로 여기서 덮어쓴다 */
  const [enabledOverride, setEnabledOverride] = useState<Record<string, boolean>>({});
  const [togglingKey, setTogglingKey] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const couponById = useMemo(() => {
    const map = new Map<string, GiftCouponForRedemptionDashboard>();
    for (const c of coupons) map.set(c._id, c);
    return map;
  }, [coupons]);

  /**
   * 품절 처리 대상 목록 — 선택 옵션(선택형) 또는 상품 원본(비선택형) 단위로 묶는다.
   * 여기서 판매를 끄지 않으면 수령자 재선택 목록에 품절 상품이 그대로 다시 뜬다.
   */
  const soldOutTargets = useMemo<SoldOutTarget[]>(() => {
    const map = new Map<string, SoldOutTarget>();
    for (const c of coupons) {
      const unit = choiceUnitOf(c);
      const info = itemInfoOf(c);
      const target: Omit<SoldOutTarget, 'count'> | null = unit
        ? {
            key: `choiceUnit:${unit._id}`,
            kind: 'choiceUnit',
            id: unit._id,
            name: unit.name,
            subName: itemNameOf(c),
            enabled: unit.enabled !== false,
          }
        : info
        ? {
            key: `itemInfo:${info._id}`,
            kind: 'itemInfo',
            id: info._id,
            name: info.name,
            subName:
              info.giftItemBrand && typeof info.giftItemBrand === 'object'
                ? info.giftItemBrand.name
                : '',
            enabled: info.isEnabled !== false,
          }
        : null;
      if (!target) continue;

      const prev = map.get(target.key);
      if (prev) prev.count += 1;
      else map.set(target.key, { ...target, count: 1 });
    }
    return Array.from(map.values()).map((t) => ({
      ...t,
      enabled: enabledOverride[t.key] ?? t.enabled,
    }));
  }, [coupons, enabledOverride]);

  const stillOnSaleCount = soldOutTargets.filter((t) => t.enabled).length;

  /** 표 행 배지용 — 이 쿠폰의 품절 대상이 아직 판매중인가 */
  const isRowOnSale = (c: GiftCouponForRedemptionDashboard) => {
    const key = targetKeyOf(c);
    return key ? soldOutTargets.find((t) => t.key === key)?.enabled === true : false;
  };

  const handleToggleTarget = async (target: SoldOutTarget, next: boolean) => {
    if (!next && target.kind === 'itemInfo') {
      const ok = confirm(
        `"${target.name}" 상품 전체의 판매를 중단합니다.\n` +
          '이 상품은 모든 상조 카탈로그에서 숨겨집니다. 계속할까요?',
      );
      if (!ok) return;
    }
    setTogglingKey(target.key);
    setToggleError(null);
    try {
      if (target.kind === 'choiceUnit') {
        await updateGiftChoiceUnit(target.id, { enabled: next }, sellerRole());
      } else {
        await updateGiftItemInfo({ _id: target.id, isEnabled: next }, sellerRole());
      }
      setEnabledOverride((prev) => ({ ...prev, [target.key]: next }));
    } catch (err: unknown) {
      setToggleError(
        err instanceof Error
          ? `판매상태 변경 실패: ${err.message}`
          : '판매상태 변경에 실패했습니다.',
      );
    } finally {
      setTogglingKey(null);
    }
  };

  // 수신번호가 없는 건은 서버가 건너뛰므로 미리 표시해 준다
  const noPhoneCount = coupons.filter((c) => !c.recipient?.phoneNumber).length;
  const sendableCount = coupons.length - noPhoneCount;

  const trimmedBase = baseUrl.trim().replace(/\/+$/, '');
  const sampleCoupon = coupons[0];
  const previewLink = giftRecipientBaseUrl
    ? giftCouponReselectRecipientUrl(sampleCoupon?._id ?? '{couponId}')
    : `${trimmedBase || '{baseUrl}'}/gift-store/coupon/${
        sampleCoupon?._id ?? '{couponId}'
      }/reselect`;

  const deadlineLabel = useMemo(() => {
    if (!deadline) return null;
    const d = new Date(`${deadline}T23:59:59+09:00`);
    if (Number.isNaN(d.getTime())) return null;
    return `${d.getMonth() + 1}월 ${d.getDate()}일`;
  }, [deadline]);

  const sampleBuyerName = buyerNameOf(sampleCoupon);

  const handlePickPreset = (id: string, presetMessage: string) => {
    setPresetId(id);
    setMessage(presetMessage);
  };

  const handleSend = async () => {
    if (coupons.length === 0) {
      setError('선택된 쿠폰이 없습니다.');
      return;
    }
    if (!message.trim()) {
      setError('안내 본문을 입력해주세요.');
      return;
    }
    if (!deadline) {
      setError('재선택 기한을 입력해주세요. (미선택 시 환불 안내에 필요)');
      return;
    }
    // 판매를 끄지 않으면 수령자 재선택 화면에 품절 상품이 그대로 다시 뜬다
    if (stillOnSaleCount > 0) {
      const ok = confirm(
        `아직 판매중인 품절 상품이 ${stillOnSaleCount}건 있습니다.\n` +
          '이대로 보내면 수령자 재선택 목록에 같은 상품이 다시 노출됩니다. 그래도 발송할까요?',
      );
      if (!ok) return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await sendReselectNotice({
        couponIds: coupons.map((c) => c._id),
        message: message.trim(),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        deadlineAt: new Date(`${deadline}T23:59:59.999+09:00`).toISOString(),
        baseUrl: baseUrl.trim() || window.location.origin,
      });
      setResult(res);
    } catch (err: unknown) {
      const statusCode = (err as { response?: { status?: number } })?.response?.status;
      setError(
        statusCode === 404
          ? '서버에 아직 반영되지 않은 기능입니다. (품절 재선택 안내 API 미배포)'
          : err instanceof Error
          ? err.message
          : '품절 안내 발송에 실패했습니다.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    onSent?.();
    onClose();
  };

  const labelOf = (couponId: string) => {
    const c = couponById.get(couponId);
    return c ? `#${c.seq} · ${c.code}` : couponId;
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative flex max-h-[94vh] w-full max-w-[1400px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        {/* 헤더 */}
        <div className="flex items-start justify-between border-b px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-amber-800">품절 안내 · 재선택 요청</h3>
            <p className="mt-0.5 text-sm text-gray-500">
              선택한 <span className="font-bold text-amber-700">{coupons.length}건</span>
              을 재선택 안내 상태로 바꾸고, 교환 신청자에게 재선택 링크를 보냅니다.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {!result ? (
          <>
            <div className="grid flex-1 grid-cols-1 gap-6 overflow-y-auto p-6 lg:grid-cols-3">
              {/* 1단: 대상 목록 + 처리 안내 */}
              <div className="space-y-4">
                {/* 대상 목록 */}
                <div>
                  <p className="mb-1.5 text-sm font-bold text-gray-700">
                    발송 대상 {coupons.length}건
                  </p>
                  <div className="max-h-[320px] overflow-y-auto rounded-lg border border-gray-200">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 text-left text-xs text-gray-500">
                        <tr>
                          <th className="px-3 py-2 font-medium">코드</th>
                          <th className="px-3 py-2 font-medium">품절 상품</th>
                          <th className="px-3 py-2 font-medium">수신번호</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {coupons.map((c) => (
                          <tr key={c._id}>
                            <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-gray-500">
                              #{c.seq} {c.code}
                            </td>
                            <td className="px-3 py-2 text-gray-800">
                              {unitNameOf(c) || itemNameOf(c)}
                              {unitNameOf(c) && (
                                <span className="ml-1 text-xs text-gray-400">
                                  {itemNameOf(c)}
                                </span>
                              )}
                              {isRowOnSale(c) && (
                                <span className="ml-1 whitespace-nowrap rounded bg-red-50 px-1 py-0.5 text-[11px] font-medium text-red-600">
                                  판매중
                                </span>
                              )}
                            </td>
                            <td
                              className={`whitespace-nowrap px-3 py-2 ${
                                c.recipient?.phoneNumber
                                  ? 'text-gray-600'
                                  : 'font-medium text-red-500'
                              }`}
                            >
                              {fmtPhone(c.recipient?.phoneNumber) || '번호 없음'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {noPhoneCount > 0 && (
                    <p className="mt-1.5 text-xs font-medium text-red-600">
                      수신번호가 없는 {noPhoneCount}건은 발송에서 제외됩니다. (실제 발송{' '}
                      {sendableCount}건)
                    </p>
                  )}
                </div>

                {/* 품절 상품 판매상태 — 여기서 바로 끌 수 있다 */}
                {soldOutTargets.length > 0 && (
                  <div>
                    <p className="mb-1.5 text-sm font-bold text-gray-700">
                      품절 상품 판매상태{' '}
                      <span className="font-normal text-gray-400">
                        — 끄지 않으면 재선택 목록에 그대로 다시 뜹니다
                      </span>
                    </p>
                    <div className="space-y-2 rounded-lg border border-gray-200 p-2.5">
                      {soldOutTargets.map((t) => (
                        <div key={t.key} className="flex items-center gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-gray-800">
                              {t.name}
                              <span className="ml-1 text-xs font-normal text-gray-400">
                                {t.kind === 'choiceUnit' ? '옵션' : '상품 전체'} ·{' '}
                                {t.count}건
                              </span>
                            </p>
                            {t.subName && (
                              <p className="truncate text-xs text-gray-400">
                                {t.subName}
                              </p>
                            )}
                          </div>
                          <span
                            className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-bold ${
                              t.enabled
                                ? 'bg-red-50 text-red-600'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {t.enabled ? '판매중' : '판매중단'}
                          </span>
                          <button
                            onClick={() => handleToggleTarget(t, !t.enabled)}
                            disabled={togglingKey === t.key}
                            className={`whitespace-nowrap rounded-lg border px-2.5 py-1.5 text-xs font-bold disabled:opacity-50 ${
                              t.enabled
                                ? 'border-amber-600 bg-amber-600 text-white hover:bg-amber-700'
                                : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {togglingKey === t.key
                              ? '변경중…'
                              : t.enabled
                              ? '판매 끄기'
                              : '되돌리기'}
                          </button>
                        </div>
                      ))}
                    </div>
                    {toggleError && (
                      <p className="mt-1.5 text-xs font-medium text-red-600">
                        {toggleError}
                      </p>
                    )}
                    {stillOnSaleCount > 0 && (
                      <p className="mt-1.5 text-xs font-medium text-red-600">
                        아직 판매중인 상품이 {stillOnSaleCount}건 있습니다. 안내 발송 전에
                        꺼주세요.
                      </p>
                    )}
                  </div>
                )}

                {/* 처리 안내 */}
                <p className="rounded-lg bg-amber-50 px-3.5 py-3 text-sm leading-relaxed text-amber-800">
                  발송 즉시 해당 건은{' '}
                  <span className="font-bold">발송대기에서 빠지고</span> 재선택 안내
                  탭으로 이동합니다. 수령자가 다른 상품을 고르면 자동으로 발송대기로
                  돌아옵니다.
                  <br />
                  품절 상품은 <span className="font-bold">위에서 판매를 끈 뒤</span>{' '}
                  안내를 보내야 재선택 목록에 다시 노출되지 않습니다.
                </p>
              </div>

              {/* 2단: 문구·기한 입력 */}
              <div className="space-y-4">
                {/* 사유 프리셋 */}
                <div>
                  <p className="mb-1.5 text-sm font-bold text-gray-700">
                    안내 사유{' '}
                    <span className="font-normal text-gray-400">
                      — 실제 사유에 맞는 문구를 고르세요 (편집 가능)
                    </span>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {RESELECT_NOTICE_PRESETS.map((preset) => (
                      <button
                        key={preset.id}
                        onClick={() => handlePickPreset(preset.id, preset.message)}
                        title={preset.hint}
                        className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition ${
                          presetId === preset.id
                            ? 'border-amber-500 bg-amber-50 text-amber-800'
                            : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                  <p className="mt-1.5 text-xs text-gray-400">
                    {RESELECT_NOTICE_PRESETS.find((p) => p.id === presetId)?.hint ??
                      '고르지 않으면 원인을 특정하지 않는 기본 문구로 나갑니다.'}
                  </p>
                </div>

                {/* 본문 */}
                <label className="block">
                  <span className="text-sm font-bold text-gray-700">안내 본문</span>
                  <textarea
                    value={message}
                    onChange={(e) => {
                      setMessage(e.target.value);
                      setPresetId('');
                    }}
                    rows={9}
                    className="mt-1.5 w-full resize-none rounded-lg border px-3.5 py-2.5 text-sm leading-relaxed focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                  />
                  <span className="mt-1 block text-xs text-gray-400">
                    기한·링크는 서버가 자동으로 덧붙입니다. 본문에 넣지 마세요.
                  </span>
                </label>

                {/* 기한 + 내부 사유 */}
                <div className="grid grid-cols-1 gap-4">
                  <label className="block">
                    <span className="text-sm font-bold text-gray-700">재선택 기한</span>
                    <input
                      type="date"
                      value={deadline}
                      onChange={(e) => setDeadline(e.target.value)}
                      className="mt-1.5 w-full rounded-lg border px-3.5 py-2.5 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                    />
                    <span className="mt-1 block text-xs text-gray-400">
                      기본 {DEFAULT_DEADLINE_DAYS}일 · 미선택 시 구매자 전액 환불 안내에
                      사용
                    </span>
                  </label>
                  <label className="block">
                    <span className="text-sm font-bold text-gray-700">
                      내부 사유 (선택)
                    </span>
                    <input
                      type="text"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="예) 스타벅스 해당 상품 단종"
                      className="mt-1.5 w-full rounded-lg border px-3.5 py-2.5 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                    />
                    <span className="mt-1 block text-xs text-gray-400">
                      수령자에게는 보이지 않습니다.
                    </span>
                  </label>
                </div>

                {/* baseUrl */}
                {giftRecipientBaseUrl ? (
                  <div className="rounded-lg bg-amber-50 px-3.5 py-2.5 text-xs text-amber-700">
                    재선택 링크는 수신자 전용 도메인(
                    <span className="font-mono font-semibold">
                      {giftRecipientBaseUrl}
                    </span>
                    )으로 발송됩니다.
                  </div>
                ) : (
                  <label className="block">
                    <span className="text-sm font-bold text-gray-700">
                      재선택 링크 도메인
                    </span>
                    <input
                      type="text"
                      value={baseUrl}
                      onChange={(e) => setBaseUrl(e.target.value)}
                      placeholder="https://bugo.gaonprime.com"
                      className="mt-1.5 w-full rounded-lg border px-3.5 py-2.5 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                    />
                  </label>
                )}
              </div>

              {/* 3단: 발송 문자 미리보기 */}
              <div className="lg:sticky lg:top-0 lg:self-start">
                <p className="mb-1.5 text-sm font-bold text-gray-700">
                  실제 발송 문자{' '}
                  <span className="font-normal text-gray-400">
                    LMS · 제목 "답례품 재선택 안내"
                  </span>
                </p>
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                  <div className="whitespace-pre-wrap break-words rounded-xl bg-white p-4 text-[15px] leading-relaxed text-gray-800 shadow-sm">
                    {message.trim() || <span className="text-gray-400">{'{본문}'}</span>}
                    {deadlineLabel && (
                      <>
                        {'\n\n'}
                        {deadlineLabel}까지 선택이 없으시면
                        {'\n'}
                        {sampleBuyerName ? `${sampleBuyerName}님께` : '상주님께'} 전액
                        환불 처리됩니다.
                      </>
                    )}
                    {'\n\n다시 선택하기\n'}
                    <span className="break-all text-amber-700">{previewLink}</span>
                  </div>
                  <p className="mt-2 text-xs text-gray-400">
                    기한 안내 2줄은 서버가 항상 덧붙입니다. 링크는 수령자별로 각각 다르게
                    들어갑니다.
                    {sampleBuyerName && ' (환불 대상 이름은 주문 건별로 들어갑니다)'}
                  </p>
                </div>

                <a
                  href={giftCouponReselectPreviewUrl(sampleCoupon?._id, {
                    message: message.trim(),
                    deadlineAt: deadline
                      ? new Date(`${deadline}T23:59:59.999+09:00`).toISOString()
                      : undefined,
                    buyerName: sampleBuyerName,
                  })}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 flex items-center justify-center rounded-lg border border-gray-300 bg-white py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-amber-700"
                >
                  수령자 화면 미리보기 (새 탭)
                </a>
              </div>
            </div>

            {/* 하단 액션 */}
            <div className="flex items-center justify-end gap-2 border-t bg-gray-50 px-6 py-3">
              {error && (
                <p className="mr-auto rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
                  {error}
                </p>
              )}
              <button
                onClick={onClose}
                disabled={submitting}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              >
                닫기
              </button>
              <button
                onClick={handleSend}
                disabled={submitting || sendableCount === 0}
                className="rounded-lg bg-amber-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {submitting ? '발송중…' : `${sendableCount}건 안내 발송`}
              </button>
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-4 overflow-y-auto p-6">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg bg-emerald-50 px-2 py-4">
                <p className="text-xs text-emerald-600">발송 성공</p>
                <p className="text-2xl font-bold text-emerald-700">{result.sentCount}</p>
              </div>
              <div className="rounded-lg bg-gray-50 px-2 py-4">
                <p className="text-xs text-gray-500">건너뜀</p>
                <p className="text-2xl font-bold text-gray-600">{result.skippedCount}</p>
              </div>
              <div className="rounded-lg bg-red-50 px-2 py-4">
                <p className="text-xs text-red-500">실패</p>
                <p className="text-2xl font-bold text-red-600">{result.failedCount}</p>
              </div>
            </div>
            <p className="text-center text-xs text-gray-400">
              총 {result.total}건 처리 (중복 제거 후)
            </p>

            {result.skipped.length > 0 && (
              <div className="rounded-lg border border-gray-200 p-3">
                <p className="mb-1 text-xs font-semibold text-gray-500">
                  건너뛴 쿠폰 ({result.skipped.length})
                </p>
                <ul className="space-y-0.5 text-sm text-gray-600">
                  {result.skipped.map((s) => (
                    <li key={s.couponId} className="flex justify-between gap-2">
                      <span>{labelOf(s.couponId)}</span>
                      <span className="text-gray-400">
                        {REASON_LABEL[s.reason] ?? s.reason}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.failed.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50/50 p-3">
                <p className="mb-1 text-xs font-semibold text-red-600">
                  발송 실패 ({result.failed.length})
                </p>
                <ul className="space-y-0.5 text-sm text-red-700">
                  {result.failed.map((f) => (
                    <li key={f.couponId} className="flex justify-between gap-2">
                      <span>{labelOf(f.couponId)}</span>
                      <span className="truncate text-red-400" title={f.reason}>
                        {f.reason}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-1.5 text-xs text-red-500">
                  실패 건도 재선택 안내 상태로는 전환되어 있습니다. 재선택 안내 탭에서
                  문자를 다시 보내거나 안내를 취소할 수 있습니다.
                </p>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={handleDone}
                className="rounded-lg bg-amber-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-amber-700"
              >
                확인
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
